import { getDashcamClient } from "../../../services/capabilitiesService";

/**
 * Probing through Manager's asynchronous dashcam routes — the replacement for the chauffeur scan routes.
 *
 * Contract: manager-api `docs/api/dashcam-async.md`. Both routes answer `202 {id}` and are read back on
 * `GET /dashcam/operations/{id}`. A screenshot is never merged server-side, so every POST is a new
 * capture and a new object under `screenshots/unassigned/`: an operation is POSTed once and polled,
 * never re-POSTed to poll.
 */

/** How many captures may be in flight at once, per scan — sixteen at once made working channels read dead. */
export const MAX_IN_FLIGHT = 4;

const POLL_FIRST_MS = 1500;
const POLL_STEADY_MS = 2000;
/**
 * How long an operation may take before the caller stops waiting. Execution is capped near 150 s
 * server-side; past this the work was most likely lost (a worker restarted mid-task stores nothing
 * and the operation reads `queued` until its ~13 min tracking expires).
 */
const MAX_WAIT_MS = 4 * 60 * 1000;
const MAX_POLL_FAILURES = 2;
/** Reads of a stored frame still without a link before giving up — signing is retried on read. */
const MAX_UNSIGNED_READS = 5;
const MAX_RATE_LIMIT_RETRIES = 3;
const DEFAULT_RETRY_AFTER_S = 5;
const MAX_RETRY_AFTER_S = 60;

/** dashcam's sentence for each `error.upstream_reason` — the upstream message itself is never relayed. */
const REASON_MESSAGES = {
  budget_exhausted: "screenshot budget expired before any candidate was attempted",
  timeout: "timed out before a frame was captured",
  connect_failed: "the device refused the RTSP connection",
  auth_rejected: "the device rejected the credentials",
  no_frame: "the stream delivered no frame",
  size_limit: "the captured frame exceeds the size cap",
  transport: "frame capture failed on the capture host",
  unavailable: "frame capture unavailable",
  request_timeout: "the screenshot request timed out",
  nul_character: "a credential contains a NUL character",
  no_exec_vantage: "no exec vantage for this device",
  no_rtsp_forward: "no RTSP forward for this device",
  forward_refused: "the RTSP forward refused the connection",
};

const CODE_MESSAGES = {
  upstream_timeout: "dashcam did not answer in time",
  upstream_unreachable: "dashcam is unreachable",
  screenshot_storage_failed: "the frame could not be stored",
};

const RATE_LIMITED = "dashcam rate limit reached — wait a minute and retry";

class Stale extends Error {}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function retryAfterMs(error) {
  const seconds = Number(error?.response?.headers?.["retry-after"]);
  const bounded =
    Number.isFinite(seconds) && seconds >= 0 ? Math.min(seconds, MAX_RETRY_AFTER_S) : DEFAULT_RETRY_AFTER_S;
  return bounded * 1000;
}

async function submit(path, body, { headers, isStale }) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const { data } = await getDashcamClient().post(path, body, headers ? { headers } : undefined);
      if (!data?.id) throw new Error("dashcam accepted the request without an operation id");
      return data.id;
    } catch (error) {
      if (error?.response?.status !== 429 || attempt >= MAX_RATE_LIMIT_RETRIES) throw error;
      await sleep(retryAfterMs(error));
      if (isStale()) throw new Stale();
    }
  }
}

async function poll(operationId, { isStale, isReady = () => true }) {
  const giveUpAt = Date.now() + MAX_WAIT_MS;
  let deadline = giveUpAt;
  let wait = POLL_FIRST_MS;
  let failures = 0;
  for (;;) {
    await sleep(wait);
    wait = POLL_STEADY_MS;
    if (isStale()) throw new Stale();
    let data = null;
    try {
      ({ data } = await getDashcamClient().get(`/operations/${operationId}`));
      failures = 0;
    } catch (error) {
      // One blip must not lose a capture still running; a 404 is final (expired, or never ours).
      if (error?.response?.status === 429) {
        wait = retryAfterMs(error);
      } else {
        failures += 1;
        if (error?.response?.status === 404 || failures > MAX_POLL_FAILURES) throw error;
      }
    }
    if (isStale()) throw new Stale();
    if (data?.status === "failed" || (data?.status === "succeeded" && isReady(data.result || {}))) return data;
    const expiresAt = Date.parse(data?.expires_at);
    if (Number.isFinite(expiresAt)) deadline = Math.min(giveUpAt, expiresAt);
    if (Date.now() >= deadline) throw new Error("dashcam did not finish in time — retry");
  }
}

function transportMessage(error) {
  const status = error?.response?.status;
  if (status === 404) return "the operation expired or is unknown";
  if (status === 429) return RATE_LIMITED;
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return error?.message || "the request failed";
}

/**
 * `rtspPort` / `apiPort` are the capture device's `rtsp_port` / `api_port` in both topologies: dashcam
 * dials from the machine's VPN IP on-prem and from the regional jump host on cloud, and those are the
 * ports reachable from there (`captureDeviceToVideoRecorderShape` resolves to the same two).
 *
 * @param {{host: string, rtspPort?: number, apiPort?: number, username: string, password: string}} device
 * @param {object} routing - From `resolveMachineRouting`.
 * @returns {object} The shared part of a dashcam request body.
 */
export function deviceTarget({ host, rtspPort, apiPort, username, password }, routing) {
  return {
    device_host: host,
    username: username ?? "",
    password: password ?? "",
    device_rtsp_port: rtspPort || 554,
    ...(apiPort ? { device_api_port: apiPort } : {}),
    ...routing,
  };
}

/**
 * Capture one frame through `POST /dashcam/screenshot-async`.
 *
 * @param {object} body - A dashcam `ScreenshotRequest`.
 * @param {{isStale?: function(): boolean}} [options] - Stops polling once the caller moved on.
 * @returns {Promise<{status: "hit", url: string, operationId: string}
 *   |{status: "empty", message: string}
 *   |{status: "failed", message: string}
 *   |{status: "stale"}>} `empty` is dashcam's `424` (no frame from that stream): an answer, not a fault.
 */
export async function captureFrame(body, { isStale = () => false } = {}) {
  try {
    const operationId = await submit("/screenshot-async", body, { isStale });
    // `succeeded` without a `url` is a signing failure; the next read mints one, a few times at most.
    let unsignedReads = 0;
    const operation = await poll(operationId, {
      isStale,
      isReady: (result) => {
        unsignedReads += 1;
        return Boolean(result.url) || unsignedReads >= MAX_UNSIGNED_READS;
      },
    });
    if (operation.status === "succeeded") {
      if (!operation.result?.url)
        return { status: "failed", message: "the frame was stored but no link could be signed" };
      return { status: "hit", url: operation.result.url, operationId };
    }

    const { code, upstream_status: upstreamStatus, upstream_reason: reason } = operation.error || {};
    const message = REASON_MESSAGES[reason] || CODE_MESSAGES[code] || code || "the capture failed";
    if (upstreamStatus === 429) return { status: "failed", message: RATE_LIMITED };
    // Only a stream that sent nothing is silence; any other failure says nothing about a camera.
    if (upstreamStatus === 424 && reason === "no_frame") return { status: "empty", message };
    return { status: "failed", message };
  } catch (error) {
    if (error instanceof Stale) return { status: "stale" };
    return { status: "failed", message: transportMessage(error) };
  }
}

/**
 * A fresh presigned link for a captured frame — the first one lives 60 s. Never captures again.
 *
 * @param {string} operationId - The `operationId` returned by {@link captureFrame}.
 * @returns {Promise<string|null>} The link, or null once the operation expired.
 */
export async function refreshFrameUrl(operationId) {
  try {
    const { data } = await getDashcamClient().get(`/operations/${operationId}`);
    return data?.result?.url || null;
  } catch {
    return null;
  }
}

/**
 * List a device's channels through `POST /dashcam/async/cameras`.
 *
 * @param {object} body - A dashcam `CamerasRequest`.
 * @param {{idempotencyKey: string, isStale?: function(): boolean}} options - One key per operator
 *   click: without it, an identical re-scan within ten minutes is handed the previous result.
 * @returns {Promise<{status: "ok", cameras: object[]}|{status: "failed", message: string}|{status: "stale"}>}
 */
export async function discoverChannels(body, { idempotencyKey, isStale = () => false }) {
  try {
    const operationId = await submit("/async/cameras", body, {
      headers: { "Idempotency-Key": idempotencyKey },
      isStale,
    });
    const operation = await poll(operationId, { isStale });
    if (operation.status === "failed") {
      const code = operation.error?.code;
      return { status: "failed", message: CODE_MESSAGES[code] || code || "channel discovery failed" };
    }
    // `succeeded` only means dashcam answered: its own verdict is inside.
    const { status_code: statusCode, body: outcome } = operation.result || {};
    if (statusCode === 429) return { status: "failed", message: RATE_LIMITED };
    if (statusCode >= 400 || outcome?.status !== "success") {
      const reason = [outcome?.failure_reason, outcome?.description].filter(Boolean).join(": ");
      return { status: "failed", message: reason || `dashcam answered HTTP ${statusCode}` };
    }
    return { status: "ok", cameras: Array.isArray(outcome.cameras) ? outcome.cameras : [] };
  } catch (error) {
    if (error instanceof Stale) return { status: "stale" };
    return { status: "failed", message: transportMessage(error) };
  }
}

/**
 * Run `worker` over `items`, at most `limit` at a time, and stop picking new items once stale.
 *
 * @param {Array} items - The work items.
 * @param {number} limit - The concurrency cap.
 * @param {function(*): Promise<void>} worker - Called once per item.
 * @param {function(): boolean} isStale - True once the run was cancelled.
 * @returns {Promise<void>} Resolves when every started worker settled.
 */
export async function runPool(items, limit, worker, isStale) {
  const queue = [...items];
  const lane = async () => {
    while (queue.length > 0 && !isStale()) {
      await worker(queue.shift());
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, queue.length) }, lane));
}
